import { AppManagerBillingPage } from '@';

// --- Constants ---
export const SDK_LOG_PREFIX = '[AppManager SDK]';
export const EVERY_30_DAYS = 'EVERY_30_DAYS';
export const ANNUAL = 'ANNUAL';
export const FEATURES_API_ENDPOINT = '/api/app-manager/plan-features';
export const PLANS_API_ENDPOINT = '/api/app-manager/plans';
export const PROCESS_PLAN_API_ENDPOINT = '/api/app-manager/plan/process';
export const PLAN_CHANGE_MODAL_DEFAULT_TITLE = 'Confirm plan change';
export const PLAN_CHANGE_MODAL_DEFAULT_DESCRIPTION = 'Are you sure you want to switch to this plan?';

export const INTERVAL_MAP = {
    [EVERY_30_DAYS]: { label: 'Monthly', short: 'mo' },
    [ANNUAL]: { label: 'Yearly', short: 'yr' }
};

// --- General Utility Functions ---
/** Deep equality check for objects/arrays */
export function deepEqual(a, b) {
    return JSON.stringify(a) === JSON.stringify(b);
}

/** Safe JSON parse with fallback */
export function safeParse(value, fallback = {}) {
    try { return JSON.parse(value); } catch { return fallback; }
}

/** Emit custom event from an element */
export function emitEvent(element, name, detail = {}) {
    element.dispatchEvent(new CustomEvent(name, {
        detail,
        bubbles: true,
        composed: true,
    }));
}

// --- Navigation Helpers ---


let topRedirectPendingUntil = 0;

export function isTopRedirectPending() {
    return Date.now() < topRedirectPendingUntil;
}

/** Navigates the current tab to `url`. @returns {boolean} false only when no URL was given. */
export function redirectTopWindow(url) {
    if (!url) {
        console.error(SDK_LOG_PREFIX, 'redirectTopWindow called without a URL');
        return false;
    }

    const isEmbedded = window.top !== window.self;
    if (!isEmbedded || typeof window.open !== 'function') {
        topRedirectPendingUntil = Date.now() + 5000;
        window.top.location.href = url;
        return true;
    }

    topRedirectPendingUntil = Date.now() + 5000;
    window.open(url, '_top');
    return true;
}

// --- Responsive Layout ---
/**
 * How many plan cards fit side by side at the current viewport width.
 *
 * Single source of truth for these breakpoints. They intentionally read the *viewport*
 * (`window.innerWidth`), because the mobile rules left in the component stylesheets are
 * plain `@media (max-width: 768px)` queries - both must measure the same thing or the
 * JS-driven widths and the CSS-driven ones disagree. Changing this to measure an
 * element's own box means moving those media queries to container queries at the same
 * time; don't do one without the other.
 *
 * @param {number} plansCount - Number of plans actually being rendered.
 * @param {boolean} largeCard - Host opted into wider cards; show one fewer.
 * @param {object} [options]
 * @param {number} [options.mobileSlides] - Slides below 640px. The plan tables pass 1.2
 *   so the next column peeks in; the card sliders show a single full-width card.
 * @param {number} [options.width] - Viewport width, injectable for tests.
 * @returns {number} Slides per view, never more than the number of plans.
 */
export function getSlidesPerView(plansCount, largeCard = false, options = {}) {
    const { mobileSlides = 1, width = window.innerWidth } = options;
    let slidesPerView;
    if (width <= 640) slidesPerView = mobileSlides;
    else if (width <= 1024) slidesPerView = 2;
    else if (width < 1440) slidesPerView = 3;
    else if (width < 1600) slidesPerView = 4;
    else slidesPerView = 5;
    if (largeCard && slidesPerView >= 4) {
        slidesPerView -= 1;
    }
    return Math.min(slidesPerView, plansCount || 1);
}

// --- Loading Skeleton ---
/**
 * Static, data-independent loading skeleton that mirrors the billing layout.
 * Shared by both the custom and Polaris billing components so the loading state is
 * managed in one place.
 *
 * @param {string} loadingLabel - Accessible, already-translated loading text.
 * @returns {string} HTML string including its own scoped <style>.
 */
export function getPlanCardsSkeletonMarkup(loadingLabel = 'Loading plans') {
    const CARD_COUNT = 4;
    const card = `
        <div class="am-skel__slide" aria-hidden="true">
            <div class="am-skel__card">
                <div class="am-skel__bar am-skel__bar--title"></div>
                <div class="am-skel__bar am-skel__bar--price"></div>
                <div class="am-skel__bar am-skel__bar--button"></div>
                <div class="am-skel__bar am-skel__bar--subtitle"></div>
                <div class="am-skel__features">
                    <div class="am-skel__bar am-skel__bar--feature"></div>
                    <div class="am-skel__bar am-skel__bar--feature"></div>
                    <div class="am-skel__bar am-skel__bar--feature"></div>
                    <div class="am-skel__bar am-skel__bar--feature"></div>
                </div>
            </div>
        </div>`;
    return `
        <style>
            :host {
                all: initial;
                font-family: -apple-system, BlinkMacSystemFont, San Francisco, Roboto, Segoe UI, Helvetica Neue, sans-serif;
                font-size: 16px !important;
            }
            .am-skel, .am-skel *, .am-skel *::before, .am-skel *::after { box-sizing: border-box; }
            .am-skel {
                margin: 0 auto;
                padding: 0;
            }
            .am-skel__sr {
                position: absolute;
                width: 1px; height: 1px;
                margin: -1px; padding: 0; border: 0;
                clip: rect(0 0 0 0);
                clip-path: inset(50%);
                overflow: hidden;
                white-space: nowrap;
            }
            .am-skel__toggle {
                display: flex;
                gap: 8px;
                justify-content: flex-start;
                margin: 32px auto 16px auto;
            }
            .am-skel__pill { width: 160px; height: 32px; border-radius: 8px; }
            .am-skel__viewport { overflow: hidden; width: 100%; }
            .am-skel__row { display: flex; align-items: stretch; }
            .am-skel__slide { flex: 0 0 100%; min-width: 0; }
            .am-skel__card {
                display: flex;
                flex-direction: column;
                gap: 12px;
                width: calc(100% - 8px);
                min-height: 400px;
                margin: 0 auto;
                padding: 16px;
                background-color: #fff;
                border: 1px solid #e5e5e5;
                border-radius: 12px;
                box-shadow: 0 1px 3px 0 rgba(0,0,0,0.07), 0 1px 2px 0 rgba(0,0,0,0.04);
            }
            .am-skel__features { display: flex; flex-direction: column; gap: 16px; margin-top: 12px; }
            .am-skel__bar {
                border-radius: 6px;
                background-color: #ecebeb;
                background-image: linear-gradient(90deg, #ecebeb 0px, #f5f4f4 60px, #ecebeb 120px);
                background-size: 240px 100%;
                background-repeat: no-repeat;
                animation: am-skel-shimmer 1.2s ease-in-out infinite;
            }
            .am-skel__bar--title { width: 55%; height: 32px; }
            .am-skel__bar--price { width: 70%; height: 20px; margin-top: 32px; }
            .am-skel__bar--button { width: 100%; height: 28px; border-radius: 8px; margin-top: 8px; }
            .am-skel__bar--subtitle { width: 50%; height: 24px; border-radius: 36px; margin-top: 12px; }
            .am-skel__bar--feature { width: 100%; height: 16px; }
            .am-skel__bar--feature:nth-child(2) { width: 80%; }
            .am-skel__bar--feature:nth-child(3) { width: 70%; }
            .am-skel__bar--feature:nth-child(4) { width: 85%; }
            .am-skel__dots {
                display: flex;
                gap: 6px;
                justify-content: center;
                margin-top: 16px;
            }
            .am-skel__dots span {
                width: 8px; height: 8px;
                border-radius: 50%;
                background-color: #e0e0e0;
            }
            /* Responsive slide widths mirror the real slider breakpoints,
               capped at 4 columns so all 4 skeleton cards are shown:
               <=640:1  641-1024:2  1025-1439:3  >=1440:4 */
            @media screen and (min-width: 641px) {
                .am-skel { max-width: 808px; }
                .am-skel__slide { flex: 0 0 50%; }
            }
            @media screen and (min-width: 769px) {
                .am-skel { padding: 0 96px; }
            }
            @media screen and (min-width: 1025px) {
                .am-skel { max-width: 1208px; }
                .am-skel__slide { flex: 0 0 33.3333%; }
            }
            @media screen and (min-width: 1440px) {
                .am-skel { max-width: 1448px; }
                .am-skel__slide { flex: 0 0 25%; }
            }
            @keyframes am-skel-shimmer {
                0% { background-position: -120px 0; }
                100% { background-position: 240px 0; }
            }
            @media (prefers-reduced-motion: reduce) {
                .am-skel__bar { animation: none; }
            }
        </style>
        <div class="am-skel" role="status" aria-busy="true" aria-live="polite">
            <span class="am-skel__sr">${escapeHTML(loadingLabel)}</span>
            <div class="am-skel__toggle" aria-hidden="true">
                <div class="am-skel__bar am-skel__pill"></div>
                <div class="am-skel__bar am-skel__pill"></div>
            </div>
            <div class="am-skel__viewport">
                <div class="am-skel__row">
                    ${Array.from({ length: CARD_COUNT }).map(() => card).join('')}
                </div>
            </div>
            <div class="am-skel__dots" aria-hidden="true">
                <span></span><span></span><span></span><span></span>
            </div>
        </div>`;
}

// --- Translation Helpers ---
export function translateMe(key) {
    const value = AppManagerBillingPage.translateMe(key);
    if (typeof window !== 'undefined') {
        (window.__APP_MANAGER_TRANSLATIONS__ ??= new Set()).add(key);
    }
    return value;
}

// --- Plan Type Helpers ---
export function isFreePlan(plan) {
    return !!plan.public && !plan.is_external_charge && typeof plan.name === 'string' && plan.name.trim().toLowerCase() === 'free';
}

/** Plans belonging on the selected billing tab. */
export function filterPlansByInterval(plans, selectedInterval, showFreeOnYearly = false) {
    const list = Array.isArray(plans) ? plans : [];
    const wantsAnnual = selectedInterval === ANNUAL;
    const freeInterval = plan => (plan.interval === ANNUAL ? ANNUAL : EVERY_30_DAYS);
    const hasAnnualFree = list.some(plan => isFreePlan(plan) && freeInterval(plan) === ANNUAL);

    return list.filter(plan => {
        if (isFreePlan(plan)) {
            const planInterval = freeInterval(plan);
            if (!wantsAnnual) return planInterval === EVERY_30_DAYS;
            if (planInterval === ANNUAL) return true;
            return showFreeOnYearly && !hasAnnualFree;
        }
        return wantsAnnual ? plan.interval === ANNUAL : plan.interval === EVERY_30_DAYS;
    });
}

// --- Interval Helpers ---
export function getIntervalLabel(interval) {
    return translateMe(INTERVAL_MAP[interval]?.label || 'Monthly');
}
export function getIntervalShortLabel(interval) {
    return translateMe(INTERVAL_MAP[interval]?.short || 'mo');
}

// --- Plan/Feature Logic ---
/**
 * Whether a promo discount applies to a plan. Empty/missing `plan_relation`
 * means all plans; otherwise only the listed plan ids.
 * @param {Object|null} promotionalDiscount
 * @param {Object} plan
 * @returns {boolean}
 */
export function promoAppliesToPlan(promotionalDiscount, plan) {
    if (!promotionalDiscount || !(promotionalDiscount.value > 0) || !plan) {
        return false;
    }
    const relation = promotionalDiscount.plan_relation;
    // No relation => applies to all plans.
    if (!Array.isArray(relation) || relation.length === 0) {
        return true;
    }
    return relation.includes(plan.id);
}

/**
 * Calculate plan price details including discounts
 * @param {Object} plan - The plan object containing price and discount information
 * @param {Object|null} promotionalDiscount - Optional promotional discount to apply
 * @returns {Object} - Plan object with calculated price details
 */
export function calculatePlanPriceWithDiscounts(plan, promotionalDiscount = null) {
    try {
        const originalPrice = parseFloat(plan.price?.toString?.() ?? plan.price);
        if (isNaN(originalPrice)) {
            console.error(SDK_LOG_PREFIX, 'Invalid plan price:', plan.price);
            return { ...plan, price: 0, strike_price: null, has_discount: false };
        }
        let finalPrice = originalPrice;
        let hasDiscount = false;
        // Only paid plans can be discounted. Priority: promo > plan discount.
        if (originalPrice > 0) {
            if (promoAppliesToPlan(promotionalDiscount, plan)) {
                hasDiscount = true;
                finalPrice = promotionalDiscount.type === 'percentage'
                    ? originalPrice - (originalPrice * promotionalDiscount.value) / 100
                    : Math.max(0, originalPrice - promotionalDiscount.value);
            } else if (plan.discount > 0) {
                hasDiscount = true;
                finalPrice = plan.discount_type === 'percentage'
                    ? originalPrice - (originalPrice * plan.discount) / 100
                    : Math.max(0, originalPrice - plan.discount);
            }
        }
        return {
            ...plan,
            price: parseFloat(finalPrice.toFixed(2)),
            strike_price: hasDiscount ? parseFloat(originalPrice.toFixed(2)) : null,
            has_discount: hasDiscount
        };
    } catch (e) {
        console.error(SDK_LOG_PREFIX, 'Error in calculatePlanPriceWithDiscounts:', e, plan, promotionalDiscount);
        return { ...plan, price: 0, strike_price: null, has_discount: false };
    }
}

/**
 * Check if the given plan is the current active plan.
 * This returns true only when there is an active charge, a currentPlan exists,
 * and the plan either matches the currentPlan id or (for non-custom plans)
 * its base_plan matches the currentPlan id. Ensures "current" is tied to an active charge.
 */
export function isCurrentPlan(plan, currentPlan, hasActiveCharge = false) {
    return hasActiveCharge
        && currentPlan
        && (
            currentPlan.id === plan.id
            || (
                !plan.is_custom
                && plan.base_plan === currentPlan.id
            )
        );
}

/** Check if the plan is the same in another interval */
export function isSamePlanInOtherInterval(shopifyPlan, plan, currentPlan) {
    return currentPlan && (plan.shopify_plans === shopifyPlan);
}

/**
 * Determine whether to show a store-base plan note below the plan.
 * Only applicable for store-base plans (`plan.store_base_plan`).
 * The note is shown when:
 * - a `currentPlan` exists,
 * - the `shopifyPlan` is listed in `plan.shopify_plans`,
 * - the plan is not the current active plan,
 * - and the plan is not the same plan in another interval.
 *
 * Use this to display the following note below the plan:
 * Note: On account of your recent Shopify plan upgrade, you should consider upgrading your current app plan
 */
export function isPlanWithNote(shopifyPlan, plan, currentPlan, hasActiveCharge) {
    return !!(
        plan.store_base_plan
        && currentPlan
        && plan.shopify_plans.includes(shopifyPlan)
        && !isCurrentPlan(plan, currentPlan, hasActiveCharge)
        && !isSamePlanInOtherInterval(shopifyPlan, plan, currentPlan)
    );
}

/**
 * Format a feature value for display
 * @param {Object} feature - Feature object
 * @param {Object} featureValues - Optional feature values
 * @returns {string}
 */
/** Decode a JSON-encoded (possibly double-encoded) feature string; non-JSON values keep the old quote strip. */
function decodeFeatureString(value) {
    if (typeof value !== "string") return "";
    let decoded = value;
    let didDecode = false;
    for (let i = 0; i < 3; i++) {
        try {
            const parsed = JSON.parse(decoded);
            if (typeof parsed !== "string") break;
            decoded = parsed;
            didDecode = true;
        } catch {
            break;
        }
    }
    return didDecode ? decoded : value.replace(/"/g, "");
}

export function formatFeature(feature, featureValues) {
    try {
        if (["double", "integer"].includes(feature?.value_type)) {
            if (feature.format === "percentage") {
                return `${feature.value}%`;
            } else if (feature.format === "count") {
                return feature.value < 0 ? translateMe("Unlimited") : feature.value;
            } else {
                return translateMe(feature.value);
            }
        }
        if (feature?.value_type === "array") {
            let values = [];
            try {
                if (Array.isArray(feature.value)) {
                    values = feature.value;
                } else if (typeof feature.value === "string") {
                    values = JSON.parse(feature.value);
                }
            } catch (e) {
                console.error(SDK_LOG_PREFIX, "Error parsing array feature value:", e, feature.value);
                return "";
            }
            return Array.isArray(values) ? values.join(",") : "";
        }
        if (feature?.value_type === "boolean") {
            return feature.value ? translateMe("Yes") : translateMe("No");
        }
        if (feature?.value_type === "string") {
            return translateMe(decodeFeatureString(feature.value));
        }
        return "";
    } catch (e) {
        console.error(SDK_LOG_PREFIX, "Error in formatFeature:", e, feature, featureValues);
        return "";
    }
}

/**
 * Determine if a plan button should be disabled
 * @param {string} shopifyPlan
 * @param {Object} plan
 * @param {boolean} hasActiveCharge
 * @param {Object} currentPlan
 * @returns {boolean}
 */
export function isPlanButtonDisabled(shopifyPlan, plan, hasActiveCharge, currentPlan) {
    if (isCurrentPlan(plan, currentPlan, hasActiveCharge)) {
        return true;
    }

    return !!(plan.store_base_plan && !plan.shopify_plans.includes(shopifyPlan));
}

/**
 * Get the button text for a plan
 * @param {string} shopifyPlan
 * @param {Object} plan
 * @param {Object} currentPlan
 * @param {boolean} isPlanTable
 * @param {boolean} hasActiveCharge
 * @returns {string}
 */
export function getPlanButtonText(shopifyPlan, plan, currentPlan, isPlanTable = false, hasActiveCharge = false) {
    const isCurrentPlanFlag = isCurrentPlan(plan, currentPlan, hasActiveCharge);
    if (isCurrentPlanFlag) {
        return translateMe("Selected plan");
    }

    if (plan.store_base_plan && !plan.shopify_plans.includes(shopifyPlan)) {
        return translateMe("Not applicable");
    }

    return translateMe("Choose plan");
}

/**
 * Filter, map, and sort plans based on interval, discount, and note logic.
 * @param {Array} plans - Array of plan objects
 * @param {string} interval - Interval constant (e.g. EVERY_30_DAYS, ANNUAL)
 * @param {object} promotionalDiscount - Discount object
 * @param {string} shopifyPlan - Shopify plan name
 * @param {object} currentPlan - Current plan object
 * @param {boolean} hasActiveCharge - Whether there is an active charge
 * @returns {{anyPlanHasNote: boolean, filteredPlans: {price: number, has_discount: boolean, strike_price: (number|null)}[], anyPlanHasDiscount: boolean}} Filtered, mapped, and sorted plans
 */
export function getFilteredSortedPlans(plans, interval, promotionalDiscount, shopifyPlan, currentPlan, hasActiveCharge) {
    let anyPlanHasDiscount = false;
    let anyPlanHasNote = false;
    let filteredPlans;
    try {
        filteredPlans = (Array.isArray(plans) ? plans : []).filter(plan => isFreePlan(plan) || plan.interval === interval)
            .map(plan => {
                let planDetails = calculatePlanPriceWithDiscounts(plan, promotionalDiscount);
                planDetails = applyActiveChargePricing(planDetails, plan.active_charge_pricing, interval);
                planDetails.discount_badge = buildDiscountBadge(planDetails, promotionalDiscount);
                if (planDetails.has_discount && !anyPlanHasDiscount) {
                    anyPlanHasDiscount = true;
                }
                if (
                    plan.store_base_plan &&
                    !anyPlanHasNote &&
                    isPlanWithNote(shopifyPlan, plan, currentPlan, hasActiveCharge)
                ) {
                    anyPlanHasNote = true;
                }
                return planDetails;
            })
            .sort((a, b) => a.price - b.price);
    } catch (e) {
        console.error(SDK_LOG_PREFIX, 'Error in getFilteredSortedPlans:', e, plans);
        filteredPlans = [];
    }
    return { filteredPlans, anyPlanHasDiscount, anyPlanHasNote };
}

export function formatString(value) {
    if (typeof value === 'string') {
        return value.toLowerCase().replace(/\s+/g, '-');
    }
    return value;
}

/**
 * Format an ISO date as a short label, e.g. "21 Sep 2026". Falls back to the
 * raw string if it can't be parsed.
 * @param {string} dateStr
 * @returns {string}
 */
export function formatDateLabel(dateStr) {
    try {
        const [year, month, day] = dateStr.split('-').map(Number);
        const d = new Date(year, month - 1, day);
        if (isNaN(d.getTime())) return dateStr;
        return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    } catch (e) {
        return dateStr;
    }
}

/**
 * For the merchant's current plan, override the card price with the server-computed
 * active-charge pricing (what Shopify actually charges). Other cards pass through.
 * @param {Object} planDetails - plan after calculatePlanPriceWithDiscounts
 * @param {Object|null} activeChargePricing - { plan_id, interval, effective_price, strike_price, is_discount_active, ... }
 * @param {string} interval - the interval being rendered
 * @returns {Object}
 */
export function applyActiveChargePricing(planDetails, activeChargePricing, interval) {
    try {
        if (
            !activeChargePricing
            || activeChargePricing.plan_id == null
            || String(activeChargePricing.plan_id) !== String(planDetails.id)
            || (activeChargePricing.interval && activeChargePricing.interval !== planDetails.interval)
        ) {
            return planDetails;
        }

        const effective = parseFloat(activeChargePricing.effective_price);
        if (isNaN(effective)) {
            return planDetails;
        }

        const rawStrike = activeChargePricing.strike_price != null ? parseFloat(activeChargePricing.strike_price) : null;
        const isActive = !!activeChargePricing.is_discount_active;
        const hasStrike = isActive && rawStrike != null && !isNaN(rawStrike);

        return {
            ...planDetails,
            price: parseFloat(effective.toFixed(2)),
            strike_price: hasStrike ? parseFloat(rawStrike.toFixed(2)) : null,
            has_discount: hasStrike,
            is_current_charge: true,
        };
    } catch (e) {
        console.error(SDK_LOG_PREFIX, 'Error in applyActiveChargePricing:', e, planDetails, activeChargePricing);
        return planDetails;
    }
}

/**
 * Format a discount badge label, e.g. "10% off all charges", "$5 off 3 charges".
 * Returns null when there is no usable discount.
 * @param {number|string} value - discount amount (percent or currency)
 * @param {string} type - 'percentage' | 'amount'
 * @param {number|string|null} durationIntervals - billing cycles (0/null = forever)
 * @returns {string|null}
 */
export function formatDiscountBadge(value, type, durationIntervals) {
    const v = parseFloat(value);
    if (isNaN(v) || v <= 0) return null;

    const num = v % 1 === 0 ? String(v) : v.toFixed(2);
    const valueLabel = type === 'amount' ? `$${num}` : `${num}%`;

    const d = parseInt(durationIntervals, 10);
    const durationLabel = (!d || isNaN(d) || d <= 0)
        ? translateMe('all charges')
        : `${d} ${translateMe(d === 1 ? 'charge' : 'charges')}`;

    return `${valueLabel} ${translateMe('off')} ${durationLabel}`;
}

/**
 * Build the discount badge descriptor for a plan card.
 * - Current plan with active discount: green variant (`active: true`) + tooltip
 *   (end date / price after) when it has an end date; no badge once it has ended.
 * - Any other plan offering a discount: blue variant (`active: false`), no tooltip.
 * @param {Object} planDetails
 * @param {Object|null} promotionalDiscount
 * @returns {{ text: string, active: boolean, tooltip: string[]|null }|null}
 */
export function buildDiscountBadge(planDetails, promotionalDiscount) {
    try {
        const acp = planDetails.active_charge_pricing;
        const isCurrentCharge = acp
            && acp.plan_id != null
            && String(acp.plan_id) === String(planDetails.id)
            && (!acp.interval || acp.interval === planDetails.interval);

        if (isCurrentCharge) {
            if (!acp.is_discount_active) return null;

            const text = formatDiscountBadge(acp.discount_value, acp.discount_type, acp.discount_duration_intervals);
            if (!text) return null;

            let tooltip = null;
            if (acp.discount_ends_on) {
                const full = acp.strike_price != null ? parseFloat(acp.strike_price) : null;
                if (full != null && !isNaN(full)) {
                    tooltip = [`$${full.toFixed(2)}/${getIntervalShortLabel(planDetails.interval)} ${translateMe('starting')} ${formatDateLabel(acp.discount_ends_on)}`];
                } else {
                    tooltip = [`${translateMe('Discount ends')} ${formatDateLabel(acp.discount_ends_on)}`];
                }
            }
            return { text, active: true, tooltip };
        }

        if (!planDetails.has_discount) {
            return null;
        }

        let value = null, type = null, duration = null;
        if (promoAppliesToPlan(promotionalDiscount, planDetails)) {
            value = promotionalDiscount.value;
            type = promotionalDiscount.type;
            duration = promotionalDiscount.duration_intervals;
        } else if (planDetails.discount > 0) {
            value = planDetails.discount;
            type = planDetails.discount_type;
            duration = planDetails.cycle_count;
        }

        const text = formatDiscountBadge(value, type, duration);
        return text ? { text, active: false, tooltip: null } : null;
    } catch (e) {
        console.error(SDK_LOG_PREFIX, 'Error in buildDiscountBadge:', e, planDetails);
        return null;
    }
}

// --- Plan Details / Highlights Helpers ---

/**
 * Normalize a sort order value.
 * Uses Number() instead of parseInt() || fallback to correctly handle sort_order: 0.
 * @param {*} value
 * @param {number} [fallback=999]
 * @returns {number}
 */
export function normalizeSortOrder(value, fallback = 999) {
    const n = Number(value);
    return Number.isNaN(n) ? fallback : n;
}

/**
 * Get highlight-type entries from plan.details, sorted by sort_order.
 * Normalized into a feature-compatible shape (value_type: 'boolean').
 * @param {Object} plan
 * @returns {Array}
 */
export function getSortedPlanHighlights(plan) {
    try {
        return Object.values(plan.details || {})
            .filter(highlight => highlight.type === 'highlight')
            .sort((a, b) => normalizeSortOrder(a.sort_order) - normalizeSortOrder(b.sort_order))
            .map(highlight => ({
                uuid: `highlight_${highlight.id}`,
                value: '1',
                value_type: 'boolean',
                name: highlight.text,
            }));
    } catch (e) {
        console.error(SDK_LOG_PREFIX, 'Error in getSortedPlanHighlights:', e, plan);
        return [];
    }
}

/**
 * Get detail-type entries from plan.details, sorted by sort_order.
 * Normalized into a feature-compatible shape (value_type: 'boolean').
 * @param {Object} plan
 * @returns {Array}
 */
export function getSortedPlanDetails(plan) {
    try {
        return Object.values(plan.details || {})
            .filter(detail => detail.type === 'detail')
            .sort((a, b) => normalizeSortOrder(a.sort_order) - normalizeSortOrder(b.sort_order))
            .map(detail => ({
                uuid: `detail_${detail.id}`,
                value: '1',
                value_type: 'boolean',
                name: detail.text,
            }));
    } catch (e) {
        console.error(SDK_LOG_PREFIX, 'Error in getSortedPlanDetails:', e, plan);
        return [];
    }
}

/** First billing detail — always shown below the plan card */
export function getPrimaryPlanDetail(plan) {
    const details = getSortedPlanDetails(plan);
    return details.length > 0 ? details[0] : null;
}

/** Remaining billing details after the first — shown in a tooltip */
export function getAdditionalPlanDetails(plan) {
    const details = getSortedPlanDetails(plan);
    return details.length > 1 ? details.slice(1) : [];
}

/**
 * Merge plan highlights + sorted plan features into a single capped list.
 * Highlights always come first; remaining slots are filled by features sorted by popularity/display_order.
 *
 * @param {Object}  plan              - Plan object (requires plan.details and plan.features)
 * @param {Array}   allFeatures       - Full feature definition list from the component
 * @param {number}  [max=4]           - Maximum items to return
 * @param {boolean} [showOnlyHighlights=false] - When true, show only highlights (if any exist); fall back to merged list otherwise
 * @returns {Array}
 */
export function preparePlanFeatures(plan, allFeatures, max = 4, showOnlyHighlights = false) {
    try {
        const highlights = getSortedPlanHighlights(plan);

        const assignedUuids = new Set(Object.keys(plan.features || {}));
        const sortedFeatures = (Array.isArray(allFeatures) ? allFeatures : [])
            .filter(feature => assignedUuids.has(feature.uuid) && !feature.hidden_feature)
            .sort((a, b) => {
                const popularityA = normalizeSortOrder(a.popularity);
                const popularityB = normalizeSortOrder(b.popularity);
                if (popularityA !== popularityB) return popularityA - popularityB;
                return normalizeSortOrder(a.display_order) - normalizeSortOrder(b.display_order);
            })
            .map(feature => ({
                ...feature,
                value: plan.features[feature.uuid]?.value ?? plan.features[feature.uuid],
            }));

        // When showOnlyHighlights is true and highlights exist, return only highlights
        if (showOnlyHighlights && highlights.length > 0) {
            return highlights;
        }

        // Default: highlights first, fill remaining slots with features
        const remaining = Math.max(0, max - highlights.length);
        return [...highlights, ...sortedFeatures.slice(0, remaining)];
    } catch (e) {
        console.error(SDK_LOG_PREFIX, 'Error in preparePlanFeatures:', e, plan);
        return [];
    }
}

export function isPlanDowngrade(plan, currentPlan, hasActiveCharge) {
    if (!hasActiveCharge || !currentPlan?.id) return false;
    const newPrice = parseFloat(plan?.price);
    const currentPrice = parseFloat(currentPlan?.price);
    return !isNaN(newPrice) && !isNaN(currentPrice) && newPrice < currentPrice;
}

/**
 * Resolve plan-change confirmation copy from plan.details.
 * Uses all records of the type ` plan-change-confirmation `, sorted by sort_order.
 * - 2+ records: first is the title, the remaining are description lines
 * - 1 record: treated as description only (title falls back to default in UI)
 */
export function getPlanChangeConfirmationContent(plan) {
    try {
        const entries = Object.values(plan?.details || {})
            .filter(detail => detail?.type === 'plan-change-confirmation')
            .sort((a, b) => normalizeSortOrder(a.sort_order) - normalizeSortOrder(b.sort_order))
            .map(detail => (typeof detail?.text === 'string' ? detail.text.trim() : ''))
            .filter(Boolean);

        if (entries.length >= 2) {
            return {
                title: entries[0],
                descriptionLines: entries.slice(1),
                // keep single-description compatibility for older consumers
                description: entries[1],
            };
        }
        if (entries.length === 1) {
            return {
                title: null,
                descriptionLines: [entries[0]],
                description: entries[0],
            };
        }
        return { title: null, descriptionLines: [], description: null };
    } catch (e) {
        console.error(SDK_LOG_PREFIX, 'Error in getPlanChangeConfirmationContent:', e, plan);
        return { title: null, descriptionLines: [], description: null };
    }
}

/**
 * Normalize modal copy for plan-change confirmation UI.
 * Accepts a precomputed confirmation payload and returns a stable shape:
 * { title, description, descriptionLines }.
 */
export function preparePlanChangeModalContent(content = {}, fallback = {}) {
    try {
        const rawLines = Array.isArray(content?.descriptionLines)
            ? content.descriptionLines
            : (typeof content?.description === 'string' && content.description.trim() ? [content.description] : []);

        const descriptionLines = rawLines
            .map(line => (typeof line === 'string' ? line.trim() : ''))
            .filter(Boolean);

        const fallbackTitle = fallback?.title ?? PLAN_CHANGE_MODAL_DEFAULT_TITLE;
        const fallbackDescription = fallback?.description ?? PLAN_CHANGE_MODAL_DEFAULT_DESCRIPTION;

        return {
            title: typeof content?.title === 'string' && content.title.trim() ? content.title.trim() : fallbackTitle,
            descriptionLines,
            description: descriptionLines[0] || (typeof content?.description === 'string' && content.description.trim() ? content.description.trim() : fallbackDescription),
        };
    } catch (e) {
        console.error(SDK_LOG_PREFIX, 'Error in preparePlanChangeModalContent:', e, content, fallback);
        return {
            title: fallback?.title ?? PLAN_CHANGE_MODAL_DEFAULT_TITLE,
            descriptionLines: [],
            description: fallback?.description ?? PLAN_CHANGE_MODAL_DEFAULT_DESCRIPTION,
        };
    }
}

export function createEmptyPlanChangeModalContent() {
    return { title: null, description: null, descriptionLines: [] };
}

/**
 * Normalize plan-change confirmation request payload emitted by child components.
 * Returns null when source/plan is invalid for the provided component list.
 */
export function getPlanChangeRequestFromEvent(event, validSources = []) {
    try {
        const source = event?.detail?.source;
        const plan = event?.detail?.plan;
        if (!Array.isArray(validSources) || !validSources.includes(source) || !plan || !plan.id) {
            return null;
        }
        return {
            source,
            plan,
            content: {
                title: event?.detail?.title || null,
                description: event?.detail?.description || null,
                descriptionLines: Array.isArray(event?.detail?.descriptionLines) ? event.detail.descriptionLines : [],
            },
        };
    } catch (e) {
        console.error(SDK_LOG_PREFIX, 'Error in getPlanChangeRequestFromEvent:', e, event, validSources);
        return null;
    }
}

export function escapeHTML(value) {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

export function formatTextWithLineBreaks(text) {
    if (typeof text !== 'string') return '';
    return escapeHTML(text).replace(/\n/g, '<br>');
}